import React, { useMemo } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  BarChart,
  Bar,
} from "recharts";

import { i18n } from "../../translate/i18n";
import { CHART_COLORS, formatDuration, formatRate, percent } from "./format";
import ChannelDonut from "./ChannelDonut";
import KpiCard from "./KpiCard";

const useStyles = makeStyles((theme) => ({
  grid: { display: "grid", gap: 16 },
  kpis: { display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" },
  card: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    padding: "16px 18px",
    minWidth: 0,
  },
  row2: { display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" },
  cardTitle: { fontSize: 13, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: theme.palette.text.primary, marginBottom: 12 },
  empty: { fontSize: 14, color: theme.palette.text.secondary, padding: "24px 0", textAlign: "center" },
  agentRow: { display: "flex", alignItems: "center", gap: 10, padding: "6px 0", fontSize: 14 },
  agentBar: { height: 8, borderRadius: 4, backgroundColor: theme.palette.primary.main },
}));

// Completa los huecos para que el eje muestre todo el periodo, no solo los días con datos.
const fillSeries = (points, from, to) => {
  const byBucket = new Map(points.map((p) => [p.bucket, p]));
  const out = [];
  if (from === to) {
    for (let h = 0; h < 24; h += 1) {
      const b = String(h).padStart(2, "0");
      out.push({ label: `${b}:00`, conversations: 0, resolved: 0, ...(byBucket.get(b) || {}) });
    }
    return out;
  }
  const d = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  while (d <= end) {
    const b = d.toISOString().slice(0, 10);
    out.push({ label: `${b.slice(8, 10)}/${b.slice(5, 7)}`, conversations: 0, resolved: 0, ...(byBucket.get(b) || {}) });
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
};

/** Pestaña «Resumen» (doc 0447 §5). */
const SummaryTab = ({ data, filters }) => {
  const classes = useStyles();
  const theme = useTheme();
  const s = data.summary || {};
  const csat = data.csat || {};
  const axis = theme.palette.text.secondary;

  const series = useMemo(() => fillSeries(data.timeseries || [], filters.from, filters.to), [data.timeseries, filters.from, filters.to]);
  const queues = (data.byQueue || []).map((q) => ({ ...q, name: q.label || i18n.t("reports.noQueue") }));
  const agents = (data.byAgent || []).filter((a) => a.id !== null).slice(0, 5);
  const maxAgent = Math.max(1, ...agents.map((a) => a.conversations));

  return (
    <div className={classes.grid}>
      <div className={classes.kpis}>
        <KpiCard label={i18n.t("reports.kpi.conversations")} value={s.conversations ?? "—"} hint={i18n.t("reports.kpi.contacts", { count: s.contacts ?? 0 })} />
        <KpiCard label={i18n.t("reports.kpi.resolved")} value={s.resolved ?? "—"} hint={`${percent(s.resolved || 0, s.conversations || 0)}%`} />
        <KpiCard label={i18n.t("reports.kpi.unattended")} value={s.unattended ?? "—"} hint={i18n.t("reports.kpi.unattendedHint")} />
        <KpiCard live label={i18n.t("reports.kpi.openNow")} value={s.openNow ?? "—"} />
        <KpiCard live label={i18n.t("reports.kpi.pendingNow")} value={s.pendingNow ?? "—"} />
      </div>

      <div className={classes.kpis}>
        <KpiCard label={i18n.t("reports.kpi.avgFirstResponse")} value={formatDuration(s.avgFirstResponseSeconds)} hint={i18n.t("reports.kpi.avgFirstResponseHint")} />
        <KpiCard label={i18n.t("reports.kpi.avgWait")} value={formatDuration(s.avgWaitSeconds)} hint={i18n.t("reports.kpi.avgWaitHint")} />
        <KpiCard label={i18n.t("reports.kpi.avgResolution")} value={formatDuration(s.avgResolutionSeconds)} hint={i18n.t("reports.kpi.avgResolutionHint")} />
        {csat.answered > 0 && (
          <KpiCard
            label={i18n.t("reports.csat.avgRate")}
            value={`${formatRate(csat.avgRate)} / 5`}
            hint={i18n.t("reports.csat.answeredCount", { count: csat.answered })}
          />
        )}
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>{i18n.t("reports.charts.overTime")}</div>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={series} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: axis }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: axis }} />
            <Tooltip />
            <Area type="monotone" dataKey="conversations" name={i18n.t("reports.kpi.conversations")} stroke={CHART_COLORS[0]} fill={CHART_COLORS[0]} fillOpacity={0.15} />
            <Area type="monotone" dataKey="resolved" name={i18n.t("reports.kpi.resolved")} stroke={CHART_COLORS[1]} fill={CHART_COLORS[1]} fillOpacity={0.12} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className={classes.row2}>
        <div className={classes.card}>
          <div className={classes.cardTitle}>{i18n.t("reports.charts.byChannel")}</div>
          <ChannelDonut rows={data.byChannel} />
        </div>

        <div className={classes.card}>
          <div className={classes.cardTitle}>{i18n.t("reports.charts.byQueue")}</div>
          {queues.length === 0 ? (
            <div className={classes.empty}>{i18n.t("reports.empty")}</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(120, queues.length * 40)}>
              <BarChart data={queues} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: axis }} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 12, fill: axis }} />
                <Tooltip />
                <Bar dataKey="conversations" name={i18n.t("reports.kpi.conversations")} radius={[0, 4, 4, 0]}>
                  {queues.map((q, i) => <Cell key={q.id || i} fill={q.color || CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className={classes.card}>
          <div className={classes.cardTitle}>{i18n.t("reports.charts.topAgents")}</div>
          {agents.length === 0 ? (
            <div className={classes.empty}>{i18n.t("reports.empty")}</div>
          ) : (
            agents.map((a) => (
              <div key={a.id} className={classes.agentRow}>
                <span style={{ width: 120, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.label}</span>
                <span style={{ flexGrow: 1 }}>
                  <div className={classes.agentBar} style={{ width: `${(a.conversations / maxAgent) * 100}%` }} />
                </span>
                <strong style={{ width: 32, textAlign: "right" }}>{a.conversations}</strong>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryTab;
